import { threadManagementSection } from "@afar-research/afar";
import type {
  OrchestrationThreadShell,
  EnvironmentId,
  ProjectId,
  ScopedProjectRef,
  ScopedThreadRef,
  ThreadId,
} from "@afar-research/contracts";
import type { SidebarThreadSortOrder } from "@afar-research/contracts/settings";

import {
  scopedProjectKey,
  scopedThreadKey,
  scopeProjectRef,
  scopeThreadRef,
} from "../environment/index.ts";
import { getThreadSortTimestamp, type ThreadSortInput } from "./threadSort.ts";

/** The largest row collection any discovery consumer may hand to the renderer. */
export const THREAD_DISCOVERY_RENDER_LIMIT = 40;

export interface ThreadDiscoveryThread
  extends
    ThreadSortInput,
    Partial<
      Pick<
        OrchestrationThreadShell,
        "management" | "latestTurn" | "session" | "hasPendingApprovals" | "hasPendingUserInput"
      >
    > {
  readonly archivedAt: string | null;
  readonly branch: string | null;
  readonly environmentId: EnvironmentId;
  readonly id: ThreadId;
  readonly projectId: ProjectId;
  readonly title: string;
}

export interface ThreadDiscoveryProject {
  readonly environmentId: EnvironmentId;
  readonly id: ProjectId;
  readonly title: string;
  readonly workspaceRoot: string;
}

export interface ThreadDiscoveryItem<TThread extends ThreadDiscoveryThread> {
  readonly disabled: boolean;
  readonly group: "live" | "pinned" | "stale";
  readonly isPinned: boolean;
  readonly isStale: boolean;
  readonly key: string;
  readonly projectTitle: string | null;
  readonly projectWorkspaceRoot: string | null;
  readonly searchTerms: ReadonlyArray<string>;
  readonly thread: TThread;
}

export interface ThreadDiscoveryResult<TThread extends ThreadDiscoveryThread> {
  readonly hasMore: boolean;
  readonly hasPrevious: boolean;
  readonly items: ReadonlyArray<ThreadDiscoveryItem<TThread>>;
  readonly nextOffset: number;
  readonly offset: number;
  readonly previousOffset: number;
  readonly totalCount: number;
}

export interface ThreadDiscoveryModel<TThread extends ThreadDiscoveryThread> {
  discover(input?: {
    readonly activeThreadRef?: ScopedThreadRef | null;
    readonly groups?: ReadonlyArray<ThreadDiscoveryItem<TThread>["group"]>;
    readonly limit?: number;
    readonly offset?: number;
    readonly projectRefs?: ReadonlyArray<ScopedProjectRef>;
    readonly query?: string;
  }): ThreadDiscoveryResult<TThread>;
}

function normalizeSearchText(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function rankSearchTerms(terms: ReadonlyArray<string>, query: string): number {
  let best = Number.NEGATIVE_INFINITY;
  for (const [index, term] of terms.entries()) {
    if (!term.includes(query)) continue;
    const matchRank = term === query ? 3 : term.startsWith(query) ? 2 : 1;
    best = Math.max(best, 1_000 - index * 100 + matchRank);
  }
  return best;
}

/**
 * Builds the bounded, non-archived discovery projection shared by shell
 * surfaces. Rows can include explicitly non-authoritative stale context.
 * Callers can search or page it, but cannot request an unbounded row collection
 * suitable for accidental direct DOM rendering.
 */
export function createThreadDiscoveryModel<TThread extends ThreadDiscoveryThread>(input: {
  readonly now: number;
  readonly environmentReadinessById?: ReadonlyMap<EnvironmentId, boolean>;
  readonly pinnedThreadRefs?: ReadonlyArray<ScopedThreadRef>;
  readonly projects: ReadonlyArray<ThreadDiscoveryProject>;
  readonly sortOrder: SidebarThreadSortOrder;
  readonly threads: ReadonlyArray<TThread>;
}): ThreadDiscoveryModel<TThread> {
  const projectByKey = new Map(
    input.projects.map((project) => [
      scopedProjectKey(scopeProjectRef(project.environmentId, project.id)),
      project,
    ]),
  );
  const pinnedThreadKeys = new Set((input.pinnedThreadRefs ?? []).map(scopedThreadKey));
  const now = input.now;
  const sectionRankByKey = new Map<string, number>();
  const items = input.threads
    .filter((thread) => thread.archivedAt === null)
    .map((thread) => {
      const project = projectByKey.get(
        scopedProjectKey(scopeProjectRef(thread.environmentId, thread.projectId)),
      );
      const key = scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id));
      const section = threadManagementSection(thread, now);
      sectionRankByKey.set(key, { active: 0, snoozed: 1, settled: 2 }[section]);
      const isPinned = pinnedThreadKeys.has(key) && section === "active";
      const isStale =
        input.environmentReadinessById !== undefined &&
        input.environmentReadinessById.get(thread.environmentId) !== true;
      return {
        disabled: isStale,
        group: isPinned ? "pinned" : isStale ? "stale" : "live",
        isPinned,
        isStale,
        key,
        projectTitle: project?.title ?? null,
        projectWorkspaceRoot: project?.workspaceRoot ?? null,
        searchTerms: [
          thread.title,
          thread.id,
          thread.branch ?? "",
          project?.title ?? "",
          project?.workspaceRoot ?? "",
          thread.environmentId,
          thread.projectId,
          ...(isPinned ? ["pinned"] : []),
          ...(isStale ? ["stale", "environment disconnected"] : []),
        ].map(normalizeSearchText),
        thread,
      } satisfies ThreadDiscoveryItem<TThread>;
    })
    .toSorted((left, right) => {
      const groupDifference =
        ({ pinned: 0, live: 1, stale: 2 } as const)[left.group] -
        ({ pinned: 0, live: 1, stale: 2 } as const)[right.group];
      if (groupDifference !== 0) return groupDifference;
      const sectionDifference =
        (sectionRankByKey.get(left.key) ?? 0) - (sectionRankByKey.get(right.key) ?? 0);
      if (sectionDifference !== 0) return sectionDifference;
      const section = threadManagementSection(left.thread, now);
      if (section === "snoozed")
        return (
          Date.parse(left.thread.management?.snoozedUntil ?? "") -
            Date.parse(right.thread.management?.snoozedUntil ?? "") ||
          left.key.localeCompare(right.key)
        );
      if (section === "settled")
        return (
          Date.parse(right.thread.management?.settledAt ?? "") -
            Date.parse(left.thread.management?.settledAt ?? "") || left.key.localeCompare(right.key)
        );
      const timestampDifference =
        getThreadSortTimestamp(right.thread, input.sortOrder) -
        getThreadSortTimestamp(left.thread, input.sortOrder);
      return timestampDifference || right.key.localeCompare(left.key);
    });
  const itemsByProjectKey = new Map<string, Array<(typeof items)[number]>>();
  for (const item of items) {
    const projectKey = scopedProjectKey(
      scopeProjectRef(item.thread.environmentId, item.thread.projectId),
    );
    const projectItems = itemsByProjectKey.get(projectKey);
    if (projectItems === undefined) {
      itemsByProjectKey.set(projectKey, [item]);
    } else {
      projectItems.push(item);
    }
  }

  return {
    discover(options = {}) {
      // An absent or empty ref list means "every project". Callers derive these refs from
      // a project list that empties whenever an Environment loses its connection, and
      // reading that as an allow-set matching nothing would blank a list mid-session.
      const projectKeys =
        options.projectRefs === undefined || options.projectRefs.length === 0
          ? null
          : new Set(options.projectRefs.map((ref) => scopedProjectKey(ref)));
      const groups = options.groups === undefined ? null : new Set(options.groups);
      const normalizedQuery = normalizeSearchText(options.query ?? "");
      const candidates =
        options.projectRefs?.length === 1
          ? (itemsByProjectKey.get(scopedProjectKey(options.projectRefs[0]!)) ?? [])
          : items;
      const matched = candidates.flatMap((item, index) => {
        if (
          projectKeys !== null &&
          !projectKeys.has(
            scopedProjectKey(scopeProjectRef(item.thread.environmentId, item.thread.projectId)),
          )
        ) {
          return [];
        }
        if (groups !== null && !groups.has(item.group)) return [];
        const rank =
          normalizedQuery.length === 0 ? 0 : rankSearchTerms(item.searchTerms, normalizedQuery);
        return rank === Number.NEGATIVE_INFINITY ? [] : [{ item, index, rank }];
      });
      if (normalizedQuery.length > 0) {
        matched.sort((left, right) => right.rank - left.rank || left.index - right.index);
      }

      const requestedLimit = Math.max(
        1,
        Math.floor(options.limit ?? THREAD_DISCOVERY_RENDER_LIMIT),
      );
      const limit = Math.min(THREAD_DISCOVERY_RENDER_LIMIT, requestedLimit);
      const maxOffset = Math.max(0, matched.length - 1);
      const offset = Math.min(Math.max(0, Math.floor(options.offset ?? 0)), maxOffset);
      const page = matched.slice(offset, offset + limit).map((entry) => entry.item);
      const activeKey = options.activeThreadRef ? scopedThreadKey(options.activeThreadRef) : null;
      const activeItem =
        activeKey === null || page.some((item) => item.key === activeKey)
          ? null
          : matched.find((entry) => entry.item.key === activeKey)?.item;
      const activeIsInjected = activeItem !== undefined && activeItem !== null;
      const consumedPageCount = activeIsInjected ? Math.max(0, limit - 1) : page.length;
      const visibleItems = activeIsInjected
        ? [activeItem, ...page.slice(0, consumedPageCount)]
        : page;

      return {
        hasMore: offset + consumedPageCount < matched.length,
        hasPrevious: offset > 0,
        items: visibleItems,
        nextOffset: Math.min(matched.length, offset + consumedPageCount),
        offset,
        previousOffset: Math.max(0, offset - limit),
        totalCount: matched.length,
      };
    },
  };
}
